<script setup lang="ts">
import type { NovaBoxBoardDetailProps } from './NovaBoxBoardDetail.types'
import { KpopFestivalId } from '@store'
const props = withDefaults(defineProps<NovaBoxBoardDetailProps>(), {
  boardTitle: '',
})

const appStore = useAppStore()
const route = useRoute()
const { date } = useDateFormat()
const slots = useSlots()
const kpopCoverDance = ref(route.params.id === KpopFestivalId.FIRST)
const walletAddress = ref(props.boardDetail.cxwaletAdres)
const nttSj = ref(props.boardDetail.nttSj)
const nttGroupId = ref(props.boardDetail.nttGroupId)
const kpopCoverDanceSecond = computed(() => {
  if (appStore.envMode === 'production') {
    return route.params.id === KpopFestivalId.SECOND_PROD
  } else {
    return route.params.id === KpopFestivalId.SECOND_DEV
  }
})
</script>

<template>
  <div class="detail-area">
    <span v-if="boardTitle" class="title">{{ boardTitle }}</span>
    <div class="detail-box">
      <div class="detail-header">
        <span class="title">{{ boardDetail.nttSj }}</span>

        <span class="date">
          {{ date({ date: boardDetail.sysRegistDt, format: 'YYYY-MM-DD' }) }}
        </span>
      </div>

      <NovaDivider :gap="16" :theme="'blue'" />

      <ClientOnly>
        <NovaBoxBoardListEventKpopDance
          v-if="kpopCoverDance"
          :wallet-address="walletAddress"
          :ntt-sj="nttSj"
          :ntt-group-id="nttGroupId"
        />
      </ClientOnly>

      <div class="detail-content">
        <div v-if="boardDetail.fileList.length" class="file-box">
          <button
            v-for="(filePath, index) in boardDetail.fileList"
            :key="index"
            type="button"
            class="download-item"
            @click="() => useDownloader(filePath.filePath)"
          >
            <div class="label">
              <NovaIcon
                :icon="{ type: 'outline', name: 'file' }"
                :size="24"
                class="label-icon"
              />

              <span class="file-name">
                {{ filePath.orignlFileNm }}
              </span>
            </div>

            <NovaIcon
              :icon="{ type: 'outline', name: 'download' }"
              :size="24"
              class="download-icon"
            />
          </button>
        </div>

        <div
          v-dompurify-html="useReplaceStrHtmlAnchorTarget(boardDetail.nttCn)"
          class="content board-detail-contents"
        />
      </div>

      <ClientOnly>
        <NovaBoxBoardListEventKpopDanceSecond
          v-if="kpopCoverDanceSecond"
          :ntt-sj="nttSj"
          :ntt-group-id="nttGroupId"
        />
      </ClientOnly>
    </div>

    <div v-if="slots['actions'] || slots['links']" class="detail-tools">
      <div v-if="slots['actions']" class="action-box">
        <slot name="actions" />
      </div>

      <div v-if="slots['links']" class="link-box">
        <slot name="links" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.detail-area {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  padding-bottom: 24px;

  > .title {
    @include text-style($text-heading2-bold);
    line-height: 40px;
  }

  > .title + .detail-box {
    margin-top: 30px;
  }

  .detail-box {
    display: flex;
    flex-direction: column;
    padding: 30px 20px;
    background-color: $color-white;
    border-radius: $radius-10;
    @include mobile {
      margin-top: 12px;
    }

    .detail-header {
      display: flex;
      flex-direction: column;
      gap: 6px;

      span {
        display: block;
        @include text-style($text-heading3-medium);
      }
      .date {
        @include text-style($text-body-12-regular);
        color: $color-text-3;
      }
    }
  }

  .detail-content {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 16px;

    .content {
      width: 100%;
    }

    .file-box {
      display: inline-flex;
      flex-direction: column;
      gap: 12px;
      padding: 8px 10px;
      border: 1px solid $color-text-9;

      .download-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        width: auto;

        > .label {
          display: flex;
          align-items: center;
          gap: 8px;

          .label-icon {
            color: $color-99;
          }

          .file-name {
            @include text-style($text-body-14-regular);
            color: $color-black;
            @include ellipsis(1);
          }
        }

        .download-icon {
          color: $color-de;
        }
      }
    }
  }

  .detail-tools {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 10px;

    .action-box {
      text-align: right;
    }

    .link-box {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
  }
  .bottom-btn-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
